import React, { useEffect, useState } from "react";
import moment from "moment";
import momentTimeZone from "moment-timezone";
import Countdown from "react-countdown";
import {
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { MdSignalWifiStatusbarConnectedNoInternet as WifiSVG } from "react-icons/md";

import MyBidRankComponent from "./BidRankComponent";
import CountdownRenderer from "./CountdownRenderer";
import { getUserData } from "../../common";
import NonEnglishAuctionCountdownWrapper from "./NonEnglishAuctionCountdownWrapper";
import { now as serverNow, nowMs } from "../../common/dateTime";

const capitalize = (str) => {
  const capitalizedWords = str
    .toLowerCase()
    .split(" ")
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    });
  return capitalizedWords.join(" ");
};

const getLocationString = (event, location) => {
  if (location) return location;
  if (event?.city && event?.country) return `${event.city}, ${event.country}`;
  if (event?.city) return `${event?.city}`;
  if (event?.country) return `${event?.country}`;
  return "";
};

const createBidListLots = ({
  lots,
  styles,
  userAmount,
  eventDetails,
  handleUserAmountChange,
  storeHandler,
  isForwardAuction,
  isOnline,
  lotBidEnded,
  refreshPanelDetails,
}) => {
  const eventFormat = eventDetails.eventFormat;
  const timeZone = momentTimeZone.tz.guess();
  const userData = getUserData();
  let currentTime = serverNow().tz(timeZone).format();

  const isEnglishAuction = eventFormat === "ENGLISH_AUCTION";
  const isJapaneseAuction = eventFormat === "JAPANESE_AUCTION";
  const isDutchAuction = eventFormat === "DUTCH_AUCTION";
  // English auction with prebid allowed, between event publish and event
  // bid start (the server accepts prebids only in this window)
  const isPrebidPeriod = () =>
    isEnglishAuction &&
    eventDetails.previewPeriod !== "NO" &&
    serverNow().isSameOrAfter(moment(eventDetails.publishDateTime)) &&
    serverNow().isBefore(moment(eventDetails.bidStartDateTime));

  const columns = [
    {
      name: "Lot No.",
      width: "90px",
      selector: (data) => String(data.lotNumber),
      cell: (data) => capitalize(String(data.lotNumber)),
      sortable: true,
    },
    {
      name: "Description",
      width: "140px",
      selector: (data) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <Tooltip
            title={String(data.shortDescription)}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: "none",
                  whiteSpace: "pre-wrap",
                  bgcolor: "background.paper",
                  color: "text.primary",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: 1,
                  fontSize: "14px",
                },
              },
            }}
          >
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
              }}
            >
              {String(data.shortDescription)}
            </span>
          </Tooltip>
          <Tooltip
            title={String(data.longDescription)}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: "none",
                  whiteSpace: "pre-wrap",
                  bgcolor: "background.paper",
                  color: "text.primary",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: 1,
                  fontSize: "14px",
                },
              },
            }}
          >
            <Button
              variant="text"
              size="small"
              sx={{
                minWidth: "24px",
                width: "24px",
                height: "24px",
                p: 0,
                color: "text.secondary",
                "&:hover": {
                  color: "text.primary",
                  bgcolor: "action.hover",
                },
              }}
            >
              <InfoIcon
                sx={{
                  fontSize: "18px",
                  color: "#3D90D7",
                  position: "absolute",
                }}
              />
            </Button>
          </Tooltip>
        </Box>
      ),
      sortable: false,
    },
    {
      name: "Location",
      width: "100px",
      selector: (data) => (
        <Tooltip title={String(data.location)}>{String(data.location)}</Tooltip>
      ),
      sortable: true,
    },
    {
      name: "Qty & Unit",
      width: "100px",
      selector: (data) =>
        String(`${Number(data.quantity)} ${data.unit.toUpperCase()}`),
      sortable: false,
    },
    {
      name: "Start Price",
      width: "110px",
      // MANDATORY preview period: hidden until the bidder's first bid
      selector: (data) =>
        data.firstBidRequired ? "XXX" : String(Number(data.startPrice)),
      sortable: true,
    },
    ...(isJapaneseAuction || isDutchAuction
      ? [
        {
          name: "Current Price",
          width: "130px",
          selector: (data) => String(parseFloat(data.currentPrice)),
          sortable: true,
        },
        {
          name: "Time Remaining",
          width: "150px",
          selector: (data, index) => {
            const currentTime = serverNow().tz(timeZone).format();
            const isLive = data.status === "live" || currentTime >= moment(data.startDateAndTime).tz(timeZone).format();

            if (!isLive) {
              return (
                <Typography className={styles.amountSecondValueUpcoming}>
                  <Countdown now={nowMs}
                    key={Math.random()}
                    date={moment(data.startDateAndTime).tz(timeZone).format()}
                    onComplete={refreshPanelDetails}
                    renderer={(...args) =>
                      CountdownRenderer(
                        data.finalizing,
                        ...args,
                        false,
                        "START"
                      )
                    }
                  />
                </Typography>
              );
            }

            const fromTime =
              data.lastUpdatedAmountAt || data.startDateAndTime;
            const countdownEndTime = moment(fromTime)
              .add(data.timeInterval, "minutes")
              .tz(timeZone)
              .format();

            return (
              <Typography
                className={
                  isLive
                    ? styles.amountSecondValueLive
                    : styles.amountSecondValueUpcoming
                }
              >
                <NonEnglishAuctionCountdownWrapper
                  classes={styles}
                  lastUpdatedAmountAt={data.lastUpdatedAmountAt}
                  startDateAndTime={data.startDateAndTime}
                  timeInterval={data.timeInterval}
                  timeZone={timeZone}
                  finalizing={data.finalizing}
                  onComplete={refreshPanelDetails}
                />
              </Typography>
            );
          },
        },
      ]
      : []),
    {
      name: `${isForwardAuction ? "INCR" : "DECR"} (${eventDetails.incrementBidAmountBy === "PERCENTAGE"
        ? "%"
        : eventDetails.currency
        })`,
      width: "112px",
      // MANDATORY preview period: hidden until the bidder's first bid
      selector: (data) =>
        data.firstBidRequired ? "XXX" : `${Number(data.increment)}`,
      sortable: true,
    },
    ...(isJapaneseAuction
      ? [
        {
          name: `Lead Bid (${eventDetails.currency})`,
          width: "134px",
          selector: (data) => {
            const startDateAndTime = moment(data.startDateAndTime);
            currentTime = moment(currentTime);

            if (startDateAndTime.isAfter(currentTime)) {
              return <span>-</span>;
            } else if (currentTime.isSameOrAfter(startDateAndTime)) {
              return `${data.highestBid === null || data.highestBid === undefined
                ? "-"
                : data.highestBid
                }`;
            } else {
              return "-";
            }
          },
          sortable: true,
        },
      ]
      : []),
    ...(isEnglishAuction
      ? [
        {
          name: "Start/End date & time",
          width: "162px",
          selector: (data, index) => {
            const currentTime = serverNow().tz(timeZone).format();
            const isLive = data.status === "live" || currentTime >= moment(data.startDateAndTime).tz(timeZone).format();

            return (
              <Typography
                className={
                  isLive
                    ? styles.amountSecondValueLive
                    : styles.amountSecondValueUpcoming
                }
              >
                <Countdown now={nowMs}
                  key={isLive ? "live" : "upcoming"}
                  className={isLive ? styles.bidLive : styles.bidUpcoming}
                  date={moment(
                    isLive
                      ? data.endDateAndTime
                      : data.startDateAndTime
                  )
                    .tz(timeZone)
                    .format()}
                  onComplete={() =>
                    isLive ? (!data.finalizing && lotBidEnded(data, index)) : refreshPanelDetails()
                  }
                  renderer={(...args) =>
                    CountdownRenderer(data.finalizing, ...args, false, isLive ? undefined : 'START')
                  }
                />
              </Typography>
            );
          },
          sortable: false,
        },
        {
          name: `Lead Bid (${eventDetails.currency})`,
          width: "134px",
          selector: (data) => {
            const startDateAndTime = moment(data.startDateAndTime);
            currentTime = moment(currentTime);

            if (startDateAndTime.isAfter(currentTime)) {
              return <span>XXX</span>;
            } else if (
              currentTime.isSameOrAfter(startDateAndTime) &&
              eventDetails.showH1L1Bid === "YES"
            ) {
              return `${data.highestBid === null ? "-" : data.highestBid}`;
            } else {
              return "-";
            }
          },
          sortable: true,
        },
        {
          name: "Status",
          width: "70px",
          selector: (data) => {
            return (
              <MyBidRankComponent
                myBidStatus={data.myBidStatus}
                eventType={eventDetails?.eventType}
                showH1L1Bid={eventDetails?.showH1L1Bid}
              />
            );
          },
          sortable: false,
        },
        {
          name: "Bid Rate",
          width: "150px",
          selector: (data) => {
            let placeholder = "";
            const startDateAndTime = moment(data.startDateAndTime);
            currentTime = moment(currentTime);

            if (startDateAndTime.isSameOrAfter(currentTime)) {
              placeholder = data.acceptedPrebid;
            }

            return (
              <TextField
                size="small"
                type="number"
                variant="outlined"
                key={data.lotNumber}
                disabled={data.finalizing}
                placeholder={placeholder}
                InputProps={{
                  inputProps: {
                    min: 0,
                    step:
                      eventDetails.variableIncrementDecrement === "NO" &&
                      !data.firstBidRequired
                        ? data.increment
                        : 1,
                    value: userAmount[`${data.lotNumber}`] || "",
                    className: styles.auctionAmountField,
                    onChange: ({ target }) => {
                      handleUserAmountChange(
                        data.lotNumber,
                        target.value,
                        userAmount
                      );
                    },
                  },
                }}
              />
            );
          },
          sortable: false,
        },
      ]
      : []),
    {
      name: "Bid Now",
      selector: (data) =>
        isOnline ? (
          data.myBidStatus.authorized === false ||
            data.auctioneerName == userData.username ||
            userData.role == "ADMIN" ? (
            <Box className={styles.auctionBidErrorBox}>Cannot Bid</Box>
          ) : data.finalizing ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "3rem",
              }}
            >
              <CircularProgress size="1.2rem" />
            </div>
          ) : (
            <Button
              disabled={
                isEnglishAuction ? userAmount[`${data.lotNumber}`] <= 0 : false
              }
              onClick={() =>
                storeHandler(data, userAmount[`${data.lotNumber}`])
              }
              className={
                isEnglishAuction && userAmount[`${data.lotNumber}`] <= 0
                  ? styles.auctionBidErrorBox
                  : isPrebidPeriod(data)
                    ? styles.auctionPrebidButton || styles.auctionBidButton
                    : styles.auctionBidButton
              }
            >
              {isPrebidPeriod(data) ? "Prebid" : "Bid Now"}
            </Button>
          )
        ) : (
          <Box className={styles.auctionBidErrorBox}>
            Network Lost{" "}
            <WifiSVG style={{ marginLeft: "5px", fontSize: "18px" }} />
          </Box>
        ),
      sortable: false,
    },
  ];

  const createdLots =
    lots?.map((lot, index) => ({
      id: index + 1,
      location: getLocationString(lot.event, lot.location),
      acceptedPrebid: lot.acceptedPrebid,
      ...lot,
    })) || [];

  return { columns, data: createdLots };
};

export default createBidListLots;
